import React, { createContext, useContext, useState, useEffect } from 'react';
import apiService from '../services/api';

const ContentContext = createContext();

export const useContent = () => {
  const context = useContext(ContentContext);
  if (!context) {
    throw new Error('useContent must be used within a ContentProvider');
  }
  return context;
};

export const ContentProvider = ({ children }) => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchContent = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getAllContent();
      setContent(data);
    } catch (err) {
      console.error('Failed to fetch content:', err);
      setError(err.message);
      // Fallback to empty content structure
      setContent({
        navbar: [],
        hero: {},
        about: {},
        aboutValues: [],
        exploreServices: {},
        portfolio: {},
        portfolioProjects: [],
        steps: {},
        stepsItems: [],
        whyChooseUs: {},
        whyChooseUsItems: [],
        contact: {},
        footer: {},
        footerLinks: [],
        pages: [],
        services: []
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContent();
    // Refresh content every 5 minutes
    const interval = setInterval(fetchContent, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Helper function to get content by key with language support
  const getContent = (section, key, lang = 'ar') => {
    if (!content) return '';
    
    const langKey = lang === 'ar' ? `${key}_ar` : `${key}_en`;
    
    if (section === 'navbar') {
      const item = content.navbar?.find(item => item.key_name === key);
      return item ? (lang === 'ar' ? item.content_ar : item.content_en) : '';
    }
    
    if (content[section]) {
      return content[section][langKey] || content[section][key] || '';
    }
    
    return '';
  };

  // Get array content (like aboutValues, stepsItems, etc.)
  const getArrayContent = (section) => {
    if (!content) return [];
    return content[section] || [];
  };

  // Get services
  const getServices = async () => {
    try {
      const services = await apiService.getServices();
      return services;
    } catch (err) {
      console.error('Failed to fetch services:', err);
      return content?.services || [];
    }
  };

  // Get portfolio
  const getPortfolio = async () => {
    try {
      const portfolio = await apiService.getPortfolio();
      return portfolio;
    } catch (err) {
      console.error('Failed to fetch portfolio:', err);
      return content?.portfolioProjects || [];
    }
  };

  return (
    <ContentContext.Provider 
      value={{ 
        content, 
        loading, 
        error, 
        fetchContent, 
        getContent, 
        getArrayContent,
        getServices,
        getPortfolio
      }}
    >
      {children}
    </ContentContext.Provider>
  );
};

